import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';

interface Template {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components: {
    type: string;
    text?: string;
    example?: {
      body_text?: string[][];
    };
  }[];
}

interface TemplateSelectorProps {
  onClose: () => void;
  onSelect: (template: Template) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({ onClose, onSelect }) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      // axios already has /api as baseURL, so just use /templates
      const response = await axios.get('/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      alert('Failed to load templates. Please check your WhatsApp Business Account ID configuration.');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['ALL', ...Array.from(new Set(templates.map(t => t.category)))];

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'ALL' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getTemplateBody = (template: Template): string => {
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    return bodyComponent?.text || 'No preview available';
  };

  const getVariableCount = (template: Template): number => {
    const body = getTemplateBody(template);
    const matches = body.match(/\{\{\d+\}\}/g);
    return matches ? matches.length : 0;
  };

  return (
    <div className="assignment-modal-overlay" onClick={onClose}>
      <div className="assignment-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '80vh' }}>
        {/* Header */}
        <div className="assignment-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>📱</span>
            <h4>Select WhatsApp Template</h4>
          </div>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        {/* Search and Filter */}
        <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
          <div className="search-container" style={{ marginBottom: '12px' }}>
            <input
              type="text"
              placeholder="🔍 Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: selectedCategory === category ? '#3b82f6' : '#f3f4f6',
                  color: selectedCategory === category ? 'white' : '#374151',
                  transition: 'all 0.2s'
                }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Template List */}
        <div className="assignment-content" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
              Loading templates...
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📱</div>
              <p>No templates found</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredTemplates.map(template => (
                <div
                  key={template.id}
                  onClick={() => onSelect(template)}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: 'white'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.backgroundColor = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <div style={{ marginBottom: '8px' }}>
                    <h5 style={{ 
                      fontSize: '15px', 
                      fontWeight: '600', 
                      color: '#111827',
                      marginBottom: '8px'
                    }}>
                      {template.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h5>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ 
                        fontSize: '11px', 
                        padding: '3px 8px', 
                        backgroundColor: '#dbeafe', 
                        color: '#1e40af', 
                        borderRadius: '12px',
                        fontWeight: '500'
                      }}>
                        {template.category}
                      </span>
                      <span style={{ 
                        fontSize: '11px', 
                        padding: '3px 8px', 
                        backgroundColor: '#f3f4f6', 
                        color: '#374151', 
                        borderRadius: '12px',
                        fontWeight: '500'
                      }}>
                        {template.language}
                      </span>
                      {getVariableCount(template) > 0 && (
                        <span style={{ 
                          fontSize: '11px', 
                          padding: '3px 8px', 
                          backgroundColor: '#fae8ff', 
                          color: '#86198f', 
                          borderRadius: '12px',
                          fontWeight: '500'
                        }}>
                          {getVariableCount(template)} variable{getVariableCount(template) > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <p style={{ 
                    fontSize: '13px', 
                    color: '#6b7280', 
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {getTemplateBody(template)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          padding: '12px 16px', 
          borderTop: '1px solid #e5e7eb', 
          backgroundColor: '#f9fafb',
          fontSize: '12px',
          color: '#6b7280',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span>💡</span>
          <span>Templates must be approved by Meta before they can be used</span>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelector;

